import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ICoinTransaction, PaginationResult, PaginationQuery, CoinTransactionQuery } from '@packages/global';
import { coinApi } from '../services/user/coin.api';
import { useCoinStore } from '../stores/coinStore';
import Pagination from '../components/Pagination';

export default function CoinsPage() {
  const { t, i18n } = useTranslation(['app', 'common']);
  const { account, loadAccount } = useCoinStore();
  const [transactions, setTransactions] = React.useState<ICoinTransaction[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [pagination, setPagination] = React.useState<PaginationResult>({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });

  const loadTransactions = async (page: number = 1, limit: number = 20) => {
    try {
      const queryData: PaginationQuery<CoinTransactionQuery> = {
        query: {},
        pagination: { page, limit, sortBy: 'createdAt', sortOrder: 'desc' }
      };
      const response = await coinApi.transactionPageList(queryData);
      setTransactions(response.items);
      setPagination(response.pagination);
    } catch (error) {
      console.error('加载交易记录失败:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAccount();
    loadTransactions();
  }, [loadAccount]);

  const handlePageChange = (page: number) => {
    loadTransactions(page, pagination.limit);
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64"><div className="text-gray-400">{t('app:coins.loading')}</div></div>;
  }

  return (
    <div className="p-6 bg-bg-page min-h-screen">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-50">{t('app:coins.title')}</h1>
        <p className="mt-1 text-sm text-gray-400">{t('app:coins.subtitle')}</p>
      </div>

      {/* {account && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-gray-800 border border-gray-700 rounded-xl shadow-sm p-6">
            <div className="text-sm text-gray-400">当前余额</div>
            <div className="text-3xl font-bold text-indigo-400 mt-2">{account.balance}</div>
          </div>
          <div className="bg-gray-800 border border-gray-700 rounded-xl shadow-sm p-6">
            <div className="text-sm text-gray-400">累计获得</div>
            <div className="text-2xl font-semibold text-green-400 mt-2">{account.totalEarned}</div>
          </div>
          <div className="bg-gray-800 border border-gray-700 rounded-xl shadow-sm p-6">
            <div className="text-sm text-gray-400">累计消费</div>
            <div className="text-2xl font-semibold text-orange-400 mt-2">{account.totalConsumed}</div>
          </div>
          <div className="bg-gray-800 border border-gray-700 rounded-xl shadow-sm p-6">
            <div className="text-sm text-gray-400">累计过期</div>
            <div className="text-2xl font-semibold text-gray-500 mt-2">{account.totalExpired}</div>
          </div>
        </div>
      )} */}

      <div className="bg-gray-800 border border-gray-700 rounded-xl shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-700">
          <h2 className="text-lg font-medium text-gray-50">{t('app:coins.transactionHistory')}</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-700">
            <thead className="bg-gray-750">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">{t('app:coins.time')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">{t('app:coins.type')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">{t('app:coins.source')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">{t('app:coins.quantity')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">{t('app:coins.balanceChange')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">{t('app:coins.description')}</th>
              </tr>
            </thead>
            <tbody className="bg-gray-800 divide-y divide-gray-700">
              {transactions.map((tx) => (
                <tr key={tx.id} className="hover:bg-gray-750">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                    {new Date(tx.createdAt).toLocaleString(i18n.language)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded-full ${tx.type === 'earn' ? 'bg-green-900 text-green-300' :
                      tx.type === 'consume' ? 'bg-orange-900 text-orange-300' :
                        tx.type === 'refund' ? 'bg-blue-900 text-blue-300' :
                          'bg-gray-700 text-gray-300'
                      }`}>
                      {t(`app:coins.transactionType.${tx.type}`)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                    {tx.sourceType}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <span className={tx.type === 'earn' || tx.type === 'refund' ? 'text-green-400' : 'text-orange-400'}>
                      {tx.type === 'earn' || tx.type === 'refund' ? '+' : '-'}{tx.amount}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                    {tx.balanceBefore} → {tx.balanceAfter}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-400">{tx.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {transactions.length === 0 && (
          <div className="text-center py-12 text-gray-500">{t('app:coins.noTransactions')}</div>
        )}
        {transactions.length > 0 && pagination.totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-700">
            <Pagination
              page={pagination.page}
              limit={pagination.limit}
              total={pagination.total}
              totalPages={pagination.totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>
    </div>
  );
}
